import React, { useEffect, useMemo, useReducer, useRef, useState, useCallback } from 'react';
import { Button, Stack } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from './API';
import { styles } from './styles';
import AddToCandidatePool from '../CandidateSourcing/AddToCandidatePool';
import { PlaylistAdd } from '@mui/icons-material';
import { InitialCandidatesState, CandidatesReducer } from './reducer';
import { ICableData, TCandidate } from './types';
import GenericTable from '../Components/GenericTable/GenericTable';
import CandidatesTableRows from './CandidatesTableRows';
import { headerStyle } from '../Candidate/config';
import { candidatesTableColumn } from './config';
import TotalSelectedIndicatorWithActions from '../Components/GenericTable/TotalSelectedIndicatorWithActions';
import ConditionsDialog from './ConditionsDialog';
import { tableSearchButtonStyles } from '../shared/commonStyles';
import CandidateExpandedContent from './CandidateExpandedContent';
import { useWebSockets } from '../Components/Utilities/useWebSockets';
import { useCandidatePoolModal } from '../CandidateSourcing/hooks/useCandidatePoolModal';
import WithPoolModal from '../CandidateSourcing/WithPoolModal';

interface Condition {
  id?: number;
  content: string;
  weight: number;
  short_label?: string;
}

interface ApiResponse {
  resHead: {
    [key: string]: string;
  };
  res: {
    conditions: Condition[];
    candidates: TCandidate[];
  };
}

export default function CandidateSourcingScreening({
  searchId,
  conditions,
  googleApiKey
}: {
  searchId: number;
  conditions: Condition[];
  googleApiKey?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentConditions, setCurrentConditions] = useState<Condition[]>(conditions);
  const [isAddingToPools, setIsAddingToPools] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [CandidatesState, dispatch] = useReducer(CandidatesReducer, InitialCandidatesState);
  const [selectedCandidate, setSelectedCandidate] = useState<TCandidate | null>(null);
  const [isConditionsDialogOpen, setIsConditionsDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState<TCandidate[]>([]);
  const [expandedPools, setExpandedPools] = useState<Set<number>>(new Set());
  const { candidatesTableState, candidatesTableSettings } = CandidatesState;
  const { selectedItemIds } = candidatesTableState;
  const expandableCellIds = [
    ...currentConditions.map((condition) => `condition_${condition.id}`),
    'matching_percent'
  ];
  const candidatePoolModalHook = useCandidatePoolModal();
  const { handlePoolClick } = candidatePoolModalHook;
  const { subscribeToChannel } = useWebSockets('CandidateSourcingChannel', (data: ICableData) => {
    if (data.kind === 'contacts_revealed') {
      refetchCandidates();
    }
  });

  const { mutate: refineScreening } = useMutation({
    mutationFn: async () => {
      const res = (await Api.refineScreening(searchId, currentConditions)) as ApiResponse;
      if (res && res.res) {
        setCurrentConditions(res.res.conditions);
      }
    },
    onSuccess: () => {
      refetchCandidates();
    }
  });

  const { refetch: refetchCandidates, isFetching: isFetchingCandidates } = useQuery({
    queryKey: [
      'search_candidates',
      searchId,
      candidatesTableSettings.page,
      candidatesTableSettings.limit
    ],
    queryFn: async () => {
      const res = (await Api.getSearchingCandidates(
        searchId,
        candidatesTableSettings.page,
        candidatesTableSettings.limit
      )) as ApiResponse;
      setTotalItems(parseInt(res.resHead['x-total-count']));
      setTotalPages(parseInt(res.resHead['x-total-pages']));
      setCandidates(res.res.candidates);
      return res.res.candidates;
    },
    initialData: [],
    refetchInterval: (data) => {
      if (!data) return 10000;
      const allAssessed = data.every(
        (candidate) =>
          candidate.matching_percent !== undefined && candidate.matching_percent !== null
      );
      return allAssessed ? false : 10000;
    },
    refetchIntervalInBackground: true
  });

  const addCandidatesToPools = () => {
    setIsAddingToPools(true);
  };

  const getDynamicColumns = () => {
    const baseColumns = [...candidatesTableColumn];
    const conditionColumns = currentConditions.map((condition) => ({
      id: `condition_${condition.id || condition.content}`,
      label: condition.short_label || condition.content,
      enabled: true,
      sortable: false,
      canToggle: false,
      tooltip: condition.content
    }));
    return [...baseColumns, ...conditionColumns];
  };

  const handleReviewContacts = useCallback(async (candidate: TCandidate) => {
    setCandidates(
      candidates.map((c) => (c.id === candidate.id ? { ...c, revealing_contacts: true } : c))
    );
    await Api.revealContacts(searchId, candidate.id);
  }, [candidates, searchId]);

  const handleSelectAll = useCallback(async () => {
    const response = await Api.allCandidateGroupIds(searchId);
    dispatch({ type: 'SET_SELECTED_ITEM_IDS', payload: (response.res as any).ids });
  }, [searchId, dispatch]);

  const handleTogglePoolsExpansion = useCallback((candidateGroupId: number) => {
    setExpandedPools(prev => {
      const newSet = new Set(prev);
      if (newSet.has(candidateGroupId)) {
        newSet.delete(candidateGroupId);
      } else {
        newSet.add(candidateGroupId);
      }
      return newSet;
    });
  }, []);

  const allItemsInPageSelected = useMemo(() => {
    return candidates.every((candidate) => selectedItemIds.includes(candidate.id));
  }, [selectedItemIds, candidates]);

  const MemoizedTableRows = useCallback((props: any) => (
      <CandidatesTableRows
        {...props}
        onRevealContacts={handleReviewContacts}
        onViewPool={handlePoolClick}
        expandedPools={expandedPools}
        onTogglePoolsExpansion={handleTogglePoolsExpansion}
      />
    ),
    [handleReviewContacts, handlePoolClick, expandedPools, handleTogglePoolsExpansion]
  );

  useEffect(() => {
    subscribeToChannel();
  }, [subscribeToChannel]);

  return (
    <Stack sx={styles.candidatesPageContainer} ref={containerRef}>
      <WithPoolModal candidatePoolModalHook={candidatePoolModalHook} refetch={refetchCandidates}>
        <Stack sx={styles.candidatesPageHeader}>Screening</Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => setIsConditionsDialogOpen(true)}
            sx={tableSearchButtonStyles}
          >
            Edit Conditions
          </Button>
        </Stack>
        <GenericTable
          label="candidates list"
          items={candidates}
          isLoadingItems={isFetchingCandidates}
          tableState={{
            ...candidatesTableState,
            columns: getDynamicColumns()
          }}
          dispatch={dispatch}
          headerStyle={headerStyle}
          paginationState={candidatesTableSettings}
          pagination={{ totalItems, totalPages }}
          TableRows={MemoizedTableRows}
          TableActionList={undefined}
          allowMultipleSelection={candidates.length ? true : false}
          titleCellId="id"
          userPreferenceSource="candidates"
          ExpandedContent={CandidateExpandedContent}
          expandableCellIds={expandableCellIds}
        />
        <TotalSelectedIndicatorWithActions
          totalSelected={selectedItemIds.length}
          parentDiv={containerRef.current}
          totalItems={totalItems}
          clearAllEnabled={selectedItemIds.length > 0}
          onSelectAll={handleSelectAll}
          canSelectAll={allItemsInPageSelected && selectedItemIds.length < totalItems}
          dispatch={dispatch}
          actionButtonId="delete-candidate-action-button"
          actionButtonsList={[
            {
              id: 'add-to-pool',
              label: 'Add to Pool',
              icon: <PlaylistAdd />,
              callback: () => {
                addCandidatesToPools();
              }
            }
          ]}
        />
        <AddToCandidatePool
          isOpen={isAddingToPools}
          handleClose={() => setIsAddingToPools(false)}
          selectedCandidateGroups={candidates.filter(candidate => selectedItemIds.includes(candidate.id)).map(candidate => candidate.candidate_group)}
          onRemoveCandidateGroup={(candidateGroupId) => {
            dispatch({ type: 'REMOVE_SELECTED_ITEM', payload: candidateGroupId });
          }}
          refetch={refetchCandidates}
        />
        <ConditionsDialog
          isOpen={isConditionsDialogOpen}
          handleClose={() => setIsConditionsDialogOpen(false)}
          conditions={currentConditions}
          onConditionsChange={setCurrentConditions}
          onRefineScreening={() => refineScreening()}
          onResetConditions={() => setCurrentConditions(conditions)}
        />
      </WithPoolModal>
    </Stack>
  );
}
